import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  ShieldCheck,
  Shield,
  ShieldOff,
  User as UserIcon,
} from 'lucide-react'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { adminApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { avatarColor } from '@/lib/utils'
import { masterPasswordRefusal } from '@/lib/deleteProtection'
import type { AdminUser, AdminUserUpdateRequest } from '@/types'

// ── Helpers ───────────────────────────────────────────────────────────────────

function RoleBadge({ user }: { user: AdminUser }) {
  const { t } = useTranslation('users')
  if (user.is_superuser) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-violet-500/15 text-violet-500 border border-violet-500/25">
        <ShieldCheck className="h-3 w-3" />
        {t('roles.superuser')}
      </span>
    )
  }
  if (user.is_staff) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-primary/15 text-primary border border-primary/25">
        <Shield className="h-3 w-3" />
        {t('roles.staff')}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold bg-muted text-muted-foreground border border-border/60">
      <UserIcon className="h-3 w-3" />
      {t('roles.user')}
    </span>
  )
}

function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation('users')
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border ${
      active
        ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/25'
        : 'bg-muted/80 text-muted-foreground border-border/60'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-emerald-500' : 'bg-muted-foreground'}`} />
      {active ? t('status.active') : t('status.inactive')}
    </span>
  )
}

// ── User Form Sheet ───────────────────────────────────────────────────────────

interface UserFormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editUser?: AdminUser | null
  onSuccess: () => void
}

function UserFormSheet({ open, onOpenChange, editUser, onSuccess }: UserFormSheetProps) {
  const { t } = useTranslation('users')
  const { t: tc } = useTranslation('common')
  const isEdit = !!editUser
  const [username, setUsername] = useState(editUser?.username ?? '')
  const [email, setEmail] = useState(editUser?.email ?? '')
  const [firstName, setFirstName] = useState(editUser?.first_name ?? '')
  const [lastName, setLastName] = useState(editUser?.last_name ?? '')
  const [password, setPassword] = useState('')
  const [saving, setSaving] = useState(false)

  // Reset form when sheet opens/changes
  useState(() => {
    setUsername(editUser?.username ?? '')
    setEmail(editUser?.email ?? '')
    setFirstName(editUser?.first_name ?? '')
    setLastName(editUser?.last_name ?? '')
    setPassword('')
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (isEdit && editUser) {
        const data: AdminUserUpdateRequest = { email, first_name: firstName, last_name: lastName }
        if (password) data.password = password
        await adminApi.updateUser(editUser.id, data)
        toast.success(t('messages.updated'))
      } else {
        await adminApi.createUser({ username, email, password, first_name: firstName, last_name: lastName })
        toast.success(t('messages.created'))
      }
      onSuccess()
      onOpenChange(false)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string; username?: string[]; email?: string[]; password?: string[] } } })
        ?.response?.data
      const detail = msg?.detail ?? msg?.username?.[0] ?? msg?.email?.[0] ?? msg?.password?.[0] ?? t('messages.operationFailed')
      toast.error(detail)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={saving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-sm border-l border-border bg-background shadow-elevation-3 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300 flex flex-col">
          <Dialog.Title className="sr-only">{isEdit ? t('form.editUser') : t('form.newUser')}</Dialog.Title>

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/60 flex-shrink-0">
            <div>
              <p className="text-sm font-semibold">{isEdit ? t('form.editUser') : t('form.newUser')}</p>
              {isEdit && <p className="text-xs text-muted-foreground">@{editUser?.username}</p>}
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <form id="user-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {!isEdit && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.username')} <span className="text-destructive">*</span></label>
                <Input value={username} onChange={e => setUsername(e.target.value)} placeholder={t('form.usernamePlaceholder')} required />
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-sm font-medium">{t('form.email')} <span className="text-destructive">*</span></label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('form.emailPlaceholder')} required />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.firstName')}</label>
                <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={t('form.firstNamePlaceholder')} />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">{t('form.lastName')}</label>
                <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder={t('form.lastNamePlaceholder')} />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                {t('form.password')} {!isEdit && <span className="text-destructive">*</span>}
                {isEdit && <span className="text-xs text-muted-foreground font-normal ml-1">{t('form.passwordKeep')}</span>}
              </label>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isEdit ? t('form.passwordEditPlaceholder') : t('form.passwordPlaceholder')}
                required={!isEdit}
              />
            </div>

          </form>

          {/* Footer */}
          <div className="flex gap-2 px-5 py-4 border-t border-border/60 flex-shrink-0">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={saving}>
              {tc('actions.cancel')}
            </Button>
            <Button type="submit" form="user-form" className="flex-1" disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? t('form.saveChanges') : t('form.createUser')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function UsersPage() {
  const { t } = useTranslation('users')
  const { user: currentUser } = useAuthStore()
  const qc = useQueryClient()

  const [sheetOpen, setSheetOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [clear2faTarget, setClear2faTarget] = useState<AdminUser | null>(null)
  // #373 — deleting a user who owns cameras is a camera delete: the API wants the master
  // password then (and answers without it when the user owns none). The field is optional
  // here; the server's own sentence is shown under it when it was needed or wrong.
  const { t: tc } = useTranslation('common')
  const [masterPassword, setMasterPassword] = useState('')
  const masterPasswordRef = useRef('')
  const [masterPasswordError, setMasterPasswordError] = useState<string | null>(null)
  const typeMasterPassword = (v: string) => { masterPasswordRef.current = v; setMasterPassword(v); setMasterPasswordError(null) }
  const resetMasterPassword = () => { masterPasswordRef.current = ''; setMasterPassword(''); setMasterPasswordError(null) }

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deleteUser(id, masterPasswordRef.current || undefined),  // #373
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success(t('messages.deleted'))
      setDeleteTarget(null)
      resetMasterPassword()
    },
    onError: (err: unknown) => {
      const refusal = masterPasswordRefusal(err)
      if (refusal) {  // #373 — the dialog stays open with the server's sentence
        setMasterPasswordError(refusal.code === 'master_password_locked' && refusal.retryAfter
          ? `${refusal.detail} ${tc('deleteProtection.retryIn', { seconds: refusal.retryAfter })}`
          : refusal.detail || tc('deleteProtection.refused'))
        return
      }
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? t('messages.deleteFailed')
      toast.error(detail)
    },
  })

  // #194 — reset 2FA for a user who lost their enrolled phone. Without this the only
  // recovery was blanking the number by hand in Django's admin site.
  const clear2faMutation = useMutation({
    mutationFn: (id: number) => adminApi.clear2fa(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      toast.success(t('messages.twoFactorCleared'))
      setClear2faTarget(null)
    },
    onError: (err: unknown) => {
      const body = (err as { response?: { data?: { error?: string; detail?: string } } })?.response?.data
      toast.error(body?.error ?? body?.detail ?? t('messages.clear2faFailed'))
    },
  })

  const openCreate = () => { setEditTarget(null); setSheetOpen(true) }
  const openEdit = (u: AdminUser) => { setEditTarget(u); setSheetOpen(true) }

  return (
    <div className="space-y-6">
      {/* Page header */}
      {/* #126 — nav bar shows "Users"; drop the redundant title/subtitle. The stats
          row below carries the dynamic counts. */}
      <div className="flex items-center justify-end gap-4">
        <Button onClick={openCreate} size="sm" className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t('newUser')}
        </Button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: t('stats.totalUsers'), value: users.length, icon: <Users className="h-4 w-4" />, accent: 'bg-primary/10', iconColor: 'text-primary', border: 'bg-primary' },
          { label: t('stats.active'), value: users.filter(u => u.is_active).length, icon: <UserIcon className="h-4 w-4" />, accent: 'bg-emerald-500/10', iconColor: 'text-emerald-600 dark:text-emerald-400', border: 'bg-emerald-500' },
          { label: t('stats.staff'), value: users.filter(u => u.is_staff).length, icon: <Shield className="h-4 w-4" />, accent: 'bg-amber-500/10', iconColor: 'text-amber-600 dark:text-amber-400', border: 'bg-amber-500' },
          { label: t('stats.superusers'), value: users.filter(u => u.is_superuser).length, icon: <ShieldCheck className="h-4 w-4" />, accent: 'bg-violet-500/10', iconColor: 'text-violet-600 dark:text-violet-400', border: 'bg-violet-500' },
        ].map(stat => (
          <Card key={stat.label} className="overflow-hidden">
            <div className={`h-0.5 w-full ${stat.border}`} />
            <CardContent className="pt-4 pb-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-label text-muted-foreground">{stat.label}</p>
                <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${stat.accent} ${stat.iconColor}`}>
                  {stat.icon}
                </div>
              </div>
              <p className="text-heading">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* User table */}
      <Card className="border-border/60">
        <CardHeader className="pb-3">
          <CardTitle className="text-body font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            {t('allUsers')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-0">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 border-t border-border/40 first:border-t-0">
                  <div className="shimmer h-8 w-8 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="shimmer h-3.5 w-32 rounded" />
                    <div className="shimmer h-3 w-48 rounded" />
                  </div>
                  <div className="shimmer h-5 w-16 rounded-full" />
                  <div className="shimmer h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Users className="h-10 w-10 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium">{t('empty.title')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('empty.message')}</p>
            </div>
          ) : (
            <div>
              {users.map((u, idx) => {
                const isSelf = u.id === currentUser?.id
                const initials = u.username.slice(0, 2).toUpperCase()
                const bg = avatarColor(u.username)
                const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ')
                const joinDate = new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

                return (
                  <div
                    key={u.id}
                    className={`flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition-colors ${idx > 0 ? 'border-t border-border/40' : ''}`}
                  >
                    {/* Avatar */}
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${bg}`}>
                      {initials}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-medium truncate">@{u.username}</span>
                        {isSelf && (
                          <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5">{t('badge.you')}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {fullName ? `${fullName} · ` : ''}{u.email}
                      </p>
                    </div>

                    {/* Badges */}
                    <div className="hidden sm:flex items-center gap-1.5 flex-shrink-0">
                      <RoleBadge user={u} />
                      <StatusBadge active={u.is_active} />
                    </div>

                    {/* Join date */}
                    <p className="hidden md:block text-xs text-muted-foreground flex-shrink-0 w-24 text-right">{joinDate}</p>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(u)} title={t('aria.editUser')}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {u.phone_verified && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setClear2faTarget(u)}
                          title={t('aria.clear2fa')}
                        >
                          <ShieldOff className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/8"
                        onClick={() => { resetMasterPassword(); setDeleteTarget(u) }}
                        disabled={isSelf}
                        title={isSelf ? t('aria.cannotSelfDelete') : t('aria.deleteUser')}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit sheet */}
      <UserFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editUser={editTarget}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['admin-users'] })}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); resetMasterPassword() } }}
        title={t('dialog.deleteTitle')}
        description={t('dialog.deleteConfirm', { username: deleteTarget?.username })}
        confirmLabel={t('dialog.deleteTitle')}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        input={{ label: t('dialog.masterPasswordOptional'), value: masterPassword, onChange: typeMasterPassword, error: masterPasswordError, optional: true }}
      />

      {/* #194 — 2FA reset confirmation */}
      <ConfirmDialog
        open={!!clear2faTarget}
        onOpenChange={(open) => { if (!open) setClear2faTarget(null) }}
        title={t('dialog.clear2faTitle')}
        description={t('dialog.clear2faConfirm', { username: clear2faTarget?.username })}
        confirmLabel={t('dialog.clear2faTitle')}
        loading={clear2faMutation.isPending}
        onConfirm={() => clear2faTarget && clear2faMutation.mutate(clear2faTarget.id)}
      />
    </div>
  )
}
